/*
 * Project: bellcloud
 * File: apps/frontend/src/components/configuration/GeneralConfigCard.tsx
 * Author: SWAN
 * Created: 2025-08-06 16:30:00
 * Description: General Configuration Card Component
 */
import React from 'react';
import styled from '@emotion/styled';
import { formatDateTime } from '@/utils/date';
import { useTranslation } from 'react-i18next';

import { Card as BaseCard, CardTitle, CardGeneralGrid } from '@/components/ui';
import { Input } from '@/components/ui/common/input/Input';
import { NumberInput } from '@/components/ui/common/input/NumberInput';
import { Toggle } from '@/components/ui/common/input/Toggle';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

interface GeneralConfigCardProps {
  configData: Record<string, unknown>;
  isEdit: boolean;
  isSkeleton: boolean;
  deviceType?: DeviceType;
  onClick?: () => void;
  onUpdate: (updated: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  hints?: Record<string, string>;
  sensorCount?: number; // 센서 개수
  constraints?: Record<string, unknown>; // 제약조건
  isClickable?: boolean; // 클릭 가능 여부
  /** Lite 전용: 2nd Span Calibration 토글 노출 여부 (활성 CO2 센서가 있을 때만 true) */
  showSecondSpanCal?: boolean;
}

const Card = styled(BaseCard)<{
  isEdit: boolean;
  isClickable: boolean;
  deviceType?: DeviceType;
  sensorCount?: number;
}>`
  width: auto;
  flex: 1;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  transition: all 0.2s ease-in-out;

  ${({ isEdit, theme }) =>
    isEdit &&
    `
    border-color: ${theme.colors.border.interactive.selected};
    background-color: ${theme.colors.surface.interactive.hover};
  `}

  ${({ isClickable, theme }) =>
    isClickable &&
    `
    &:hover {
      background-color: ${theme.colors.surface.interactive.hover};
      border-color: ${theme.colors.border.interactive.hover};
      box-shadow: ${theme.shadows.sm};
    }
  `}
`;

const Skeleton = styled.div`
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const WarningText = styled.span`
  color: ${({ theme }) => {
    return theme.colors.status.danger.stroke.border;
  }};
  font-size: 16px;
  font-style: italic;
  max-width: 400px;
  line-height: 1.2;
  white-space: nowrap;
`;

const TimestampText = styled.span`
  font-size: 16px;
  color: ${({ theme }) => {
    return theme.colors.text.secondary;
  }};
`;

export const GeneralConfigCard: React.FC<GeneralConfigCardProps> = ({
  configData,
  isEdit,
  isSkeleton,
  deviceType = DeviceType.LITE,
  onClick,
  onUpdate,
  errors = {},
  hints = {},
  sensorCount = 4,
  constraints,
  isClickable,
  showSecondSpanCal = true,
}) => {
  const { t } = useTranslation('config');
  // isClickable이 명시되지 않았으면 onClick이 있는지로 판단
  const clickable = isClickable !== undefined ? isClickable : !!onClick;

  if (isSkeleton) {
    return (
      <Card isEdit={false} isClickable={false}>
        <Skeleton />
      </Card>
    );
  }

  const handleChange = (field: string, value: string | number | boolean) => {
    if (field === 'intelliflash_interval') {
      onUpdate({ [field]: value, confidence_interval: value });
    } else if (field === 'confidence_interval') {
      onUpdate({ [field]: value, intelliflash_interval: value });
    } else {
      onUpdate({ [field]: value });
    }
  };

  return (
    <Card isEdit={isEdit} isClickable={clickable} deviceType={deviceType} onClick={onClick}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <CardTitle>{t('general')}</CardTitle>
          {/* Warning message for non-English text */}
          {deviceType === DeviceType.LITE &&
            (() => {
              const hasNonEnglishText =
                (configData.worker_name &&
                  typeof configData.worker_name === 'string' &&
                  !/^[a-zA-Z0-9\-_]+$/.test(configData.worker_name)) ||
                (configData.location_name &&
                  typeof configData.location_name === 'string' &&
                  !/^[a-zA-Z0-9\-_]+$/.test(configData.location_name));

              return hasNonEnglishText ? (
                <WarningText>{t('warning_nonEnglishLCD')}</WarningText>
              ) : null;
            })()}
        </div>
        <TimestampText>
          {configData.timestamp ? formatDateTime(configData.timestamp as string) : ''}
        </TimestampText>
      </div>
      <CardGeneralGrid sensorCount={sensorCount} deviceType={deviceType}>
        <Input
          label={t('workerName')}
          value={(configData.worker_name as string) || ''}
          onChange={value => {
            handleChange('worker_name', value);
          }}
          disabled={!isEdit}
          minLength={0}
          maxLength={undefined}
          placeholder={t('placeholder_workerName')}
          error={errors.worker_name ? t(errors.worker_name) : undefined}
          hint={hints.worker_name || t('hint_workerName')}
        />
        <Input
          label={t('locationName')}
          value={(configData.location_name as string) || ''}
          onChange={value => {
            handleChange('location_name', value);
          }}
          disabled={!isEdit}
          minLength={0}
          maxLength={undefined}
          placeholder={t('placeholder_locationName')}
          error={errors.location_name ? t(errors.location_name) : undefined}
          hint={hints.location_name || t('hint_locationName')}
        />
        {/* Lite: Startup message input */}
        {deviceType === DeviceType.LITE && (
          <Input
            label={t('startupMessage')}
            value={(configData.startup_message as string) || ''}
            onChange={value => handleChange('startup_message', value)}
            disabled={!isEdit}
            isEdit={isEdit}
            name="startup_message"
            error={errors.startup_message ? t(errors.startup_message) : undefined}
            hint={hints.startup_message || t('hint_startupMessage')}
            maxLength={16}
            placeholder={t('placeholder_startupMessage')}
          />
        )}
        <Toggle
          label={t('stealthMode')}
          value={!!configData.stealth_mode}
          onChange={val => handleChange('stealth_mode', val ? 1 : 0)}
          isEdit={isEdit}
          name="stealth_mode"
        />
        <Toggle
          label={t('safeMode')}
          value={!!configData.safe_mode}
          onChange={val => handleChange('safe_mode', val ? 1 : 0)}
          isEdit={isEdit}
          name="safe_mode"
        />
        <Toggle
          label={t('latchingAlarm')}
          value={!!configData.latching_alarm}
          onChange={val => handleChange('latching_alarm', val ? 1 : 0)}
          isEdit={isEdit}
          name="latching_alarm"
        />
        <Toggle
          label={t('lowAlarmAck')}
          value={!!configData.low_alarm_ack}
          onChange={val => handleChange('low_alarm_ack', val ? 1 : 0)}
          isEdit={isEdit}
          name="low_alarm_ack"
        />
        <Toggle
          label={t('confidenceBeep')}
          value={!!configData.confidence_beep}
          onChange={val => handleChange('confidence_beep', val ? 1 : 0)}
          isEdit={isEdit}
          name="confidence_beep"
        />
        {/* green_light 토글 (Lite와 Mini 모두 지원) */}
        <Toggle
          label={t('greenLight')}
          value={!!configData.green_light}
          onChange={val => handleChange('green_light', val ? 1 : 0)}
          isEdit={isEdit}
          name="green_light"
        />
        {/* Lite: 2차 스팬 교정 토글 (활성 CO2 센서가 있을 때만 노출) */}
        {deviceType === DeviceType.LITE && showSecondSpanCal && (
          <Toggle
            label={t('2ndSpanCal')}
            value={!!configData.span_cal_and_bump}
            onChange={val => handleChange('span_cal_and_bump', val ? 1 : 0)}
            isEdit={isEdit}
            name="span_cal_and_bump"
          />
        )}
        <NumberInput
          label={t('datalogInterval')}
          value={configData.datalog_interval as number}
          onChange={value => handleChange('datalog_interval', value)}
          disabled={!isEdit}
          isEdit={isEdit}
          unit={(constraints?.datalog_interval as { unit?: string })?.unit || 'sec'}
          name="datalog_interval"
          error={errors.datalog_interval ? t(errors.datalog_interval) : undefined}
          hint={hints.datalog_interval}
          step={1}
        />
        <NumberInput
          label={t('confidenceBeepAndGreenLightInterval')}
          value={configData.confidence_interval as number}
          onChange={value => handleChange('confidence_interval', value)}
          disabled={!isEdit}
          isEdit={isEdit}
          unit={
            (constraints?.intelliflash_interval as { unit?: string })?.unit ||
            (constraints?.confidence_interval as { unit?: string })?.unit ||
            'sec'
          }
          name="confidence_interval"
          error={errors.confidence_interval ? t(errors.confidence_interval) : undefined}
          hint={hints.confidence_interval}
          step={1}
        />
      </CardGeneralGrid>
    </Card>
  );
};
